import { useState, useMemo } from "react"
import { Link } from "@tanstack/react-router"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { AppLogger } from '@/lib/logger'

const log = AppLogger.getLogger(import.meta)
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useApiQuery, useApiMutation } from "@/api/ApiQueryServiceProvider"
import { Search, Edit, Trash2, MapPin, ChevronLeft, ChevronRight, Loader2, Plus } from "lucide-react"
import type { NetworkCorridor, GetNetworkCorridorsResponse } from "@/types/sensNet"
import { useToast } from "@/contexts/ToastContext"

/**
 * NetworkCorridorList Component
 *
 * Displays a list of network corridors with:
 * - Search functionality by corridor name
 * - Pagination (10 items per page)
 * - Add, Edit, and Delete actions via dialogs
 * - Displays corridor details: name, counties, zipcodes, partner organizations, site count
 * - Loading and error states
 * - Toast notifications for mutations
 *
 * @returns {JSX.Element} The network corridors list component
 */
export type NetworkCorridorListProps = {
  onAdd?: () => void
  onEdit?: (corridor: NetworkCorridor) => void
}

export function NetworkCorridorList({ onAdd, onEdit }: NetworkCorridorListProps = {}) {
  const { toast } = useToast()
  const [searchQuery, setSearchQuery] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [selectedCorridor, setSelectedCorridor] = useState<NetworkCorridor | null>(null)
  const itemsPerPage = 10

  // Fetch corridors from API
  const { data, isLoading, isError, refetch } = useApiQuery<GetNetworkCorridorsResponse>(
    "getNetworkCorridors",
    {}
  )

  // Fetch organizations to resolve partner org NUMBERS → names for the Partner column.
  const { data: orgsData } = useApiQuery('getOrganizations', {}, {})
  const orgNameByNumber = useMemo(() => {
    const m = new Map<number, string>()
    const orgs = (orgsData as any)?.content?.organizations ?? []
    for (const o of orgs) {
      if (o?.orgNumber != null) m.set(Number(o.orgNumber), o.name ?? String(o.orgNumber))
    }
    return m
  }, [orgsData])

  // Delete mutation
  const deleteCorridor = useApiMutation('deleteNetworkCorridor', 'DELETE', {
    onSuccess: () => {
      setIsDeleteDialogOpen(false)
      setSelectedCorridor(null)
      refetch()
    },
    onError: (error) => {
      log.error('Failed to delete corridor:', error)
      toast({ title: 'Error', description: `Failed to delete corridor: ${error.message}`, type: 'error' })
    },
  })

  // Map API response to component state
  // Backend returns { networkCorridors: [...] } with fields: id, networkCorridorNumber, networkCorridorName, etc.
  const rawCorridors = data?.content?.networkCorridors || (data as any)?.content?.corridors || (data as any)?.networkCorridors || (data as any)?.corridors || []
  const corridors: NetworkCorridor[] = rawCorridors.map((c: any) => ({
    id: c.id,
    networkCorridorNumber: c.networkCorridorNumber,
    name: c.networkCorridorName || c.name || '',
    description: c.description || '',
    state: c.state || undefined,
    countiesCovered: c.countiesCovered || '',
    zipcodesCovered: c.zipcodesCovered || '',
    siteNumber: c.siteNumber || '',
    // Backend returns partner orgs as `partnerOrganizationNumbers` (renamed from
    // networkPartnerOrganizationIds); read that first so the count isn't always "No partners".
    networkPartner: c.partnerOrganizationNumbers || c.networkPartnerOrganizationIds || c.networkPartner || [],
    numberOfPotentialSites: c.numberOfPotentialSites || 0,
    expectedCorridorSize: c.expectedCorridorSize || 0,
    createdAt: c.createdAt,
    updatedAt: c.updatedAt,
  }))

  const filteredCorridors = corridors.filter((corridor) =>
    corridor.name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const totalPages = Math.ceil(filteredCorridors.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const paginatedCorridors = filteredCorridors.slice(startIndex, startIndex + itemsPerPage)

  const handleEdit = (corridor: NetworkCorridor) => {
    onEdit?.(corridor)
  }

  const handleDelete = (corridor: NetworkCorridor) => {
    setSelectedCorridor(corridor)
    setIsDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = () => {
    if (selectedCorridor) {
      deleteCorridor.mutate({ networkCorridorNumber: selectedCorridor.networkCorridorNumber })
    }
  }

  // Get sites count for a corridor (would need to fetch sites and filter by corridorId)
  // For now, we'll show numberOfPotentialSites as a placeholder
  const getSitesCount = (corridor: NetworkCorridor) => {
    // TODO: Fetch sites filtered by corridorId to get actual count
    return corridor.numberOfPotentialSites || 0
  }

  if (isLoading) {
    return (
      <Card className="shadow-sm">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    )
  }

  if (isError) {
    return (
      <Card className="shadow-sm">
        <CardContent className="flex flex-col items-center justify-center py-12 gap-4">
          <p className="text-destructive">Failed to load network corridors</p>
          <Button variant="outline" onClick={() => refetch()}>
            Try Again
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <Card className="shadow-sm pb-0">
        <CardHeader className="border-b bg-slate-700 text-white [&_p]:text-slate-300">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <MapPin className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-xl">Network Corridors</CardTitle>
                <CardDescription className="text-base">Manage network corridors and coverage areas</CardDescription>
              </div>
            </div>

            <Button onClick={() => onAdd?.()}>
              <Plus className="mr-2 h-4 w-4" />
              Add Corridor
            </Button>
          </div>
        </CardHeader>
      </Card>

      <Card className="shadow-sm pb-0">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search corridors..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value)
                  setCurrentPage(1)
                }}
                className="pl-9 max-w-[320px]"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm pb-0">
        <CardContent className="p-4">
        {/* Table */}
        <Table className="text-base">
          <TableHeader>
            <TableRow className="bg-muted/30 hover:bg-muted/30">
              <TableHead className="font-semibold text-base">Name</TableHead>
              <TableHead className="font-semibold text-base">Counties</TableHead>
              <TableHead className="font-semibold text-base">Zipcodes</TableHead>
              <TableHead className="font-semibold text-base">Partner Organizations</TableHead>
              <TableHead className="font-semibold text-base">Sites Count</TableHead>
              <TableHead className="w-[120px] text-right font-semibold text-base">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedCorridors.length > 0 ? (
              paginatedCorridors.map((corridor) => (
                <TableRow key={corridor.networkCorridorNumber} className="transition-colors hover:bg-muted/50">
                  <TableCell className="font-medium text-base">
                    <Link
                      to="/sensnet/network-corridors/$corridorNumber"
                      params={{ corridorNumber: corridor.networkCorridorNumber ?? 0 }}
                      className="text-primary underline-offset-4 hover:underline"
                    >
                      {corridor.name}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <span className="text-base">{corridor.countiesCovered}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-base">{corridor.zipcodesCovered}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-base">
                      {corridor.networkPartner.length > 0
                        ? corridor.networkPartner
                            .map((n: any) => orgNameByNumber.get(Number(n)) ?? `#${n}`)
                            .join(', ')
                        : 'No partners'
                      }
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-base font-medium">{getSitesCount(corridor)}</span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        asChild
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                      >
                        <Link
                          to="/sensnet/network-corridors/$networkCorridorNumber/edit"
                          params={{ networkCorridorNumber: String(corridor.networkCorridorNumber) }}
                        >
                          <Edit className="h-4 w-4" />
                          <span className="sr-only">Edit {corridor.name}</span>
                        </Link>
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
                        onClick={() => handleDelete(corridor)}
                      >
                        <Trash2 className="h-4 w-4" />
                        <span className="sr-only">Delete {corridor.name}</span>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                  No network corridors found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t py-3">
            <p className="text-base text-muted-foreground">
              Showing {startIndex + 1} to{" "}
              {Math.min(startIndex + itemsPerPage, filteredCorridors.length)} of{" "}
              {filteredCorridors.length} corridors
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-medium">Page {currentPage} of {totalPages}</span>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
        </CardContent>
      </Card>

      {/* Delete Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Network Corridor</DialogTitle>
            <DialogDescription>
              {selectedCorridor && (
                <>
                  This corridor has {getSitesCount(selectedCorridor)} associated sites. 
                  Deleting will cascade delete those sites.
                  <br /><br />
                  Are you sure you want to delete "{selectedCorridor.name}"?
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteDialogOpen(false)
                setSelectedCorridor(null)
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleteCorridor.isPending}
            >
              {deleteCorridor.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
